import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WidgetScreen } from "../../common/WidgetScreen";

import { WidgetOverviewSettings } from "../WidgetOverviewSettings";
import { useWidgetEmployeeRoleOverviewPreviewQuery } from "./useWidgetEmployeeRoleOverviewPreviewQuery";
import { WidgetEmployeeRoleOverviewPreview } from "./WidgetEmployeeRoleOverviewPreview";
import { WSEmployeeRoleOverviewMutation } from "./__generated__/WSEmployeeRoleOverviewMutation.graphql";
import { WSEmployeeRoleOverviewQuery } from "./__generated__/WSEmployeeRoleOverviewQuery.graphql";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";

export type WSEmployeeRoleOverviewParams = object;

const WS_EMPLOYEE_ROLE_OVERVIEW_QUERY = graphql`
  query WSEmployeeRoleOverviewQuery($id: ID!) {
    store {
      ...WidgetOverviewSettings_Store
      publicTeams {
        id
      }
    }
    widgetEmployeeRoleOverview(id: $id) {
      id
      teams {
        id
      }
    }
  }
`;

export const WSEmployeeRoleOverview = withWebModal(
  WSEmployeeRoleOverviewC,
  true
);
function WSEmployeeRoleOverviewC(
  props: UserLoggedInNavigatorScreenProps<"WSEmployeeRoleOverview">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWSEmployeeRoleOverview {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWSEmployeeRoleOverview(
  props: UserLoggedInNavigatorScreenProps<"WSEmployeeRoleOverview"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WSEmployeeRoleOverviewQuery>(
    WS_EMPLOYEE_ROLE_OVERVIEW_QUERY,
    {
      id: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [teamIds, _setTeamIds] = useState<string[]>(
    (data.widgetEmployeeRoleOverview?.teams.length ?? 0) ===
      data.store.publicTeams.length
      ? []
      : (data.widgetEmployeeRoleOverview?.teams.map(({ id }) => id) ?? [])
  );

  const [save, loading] = useMutation<WSEmployeeRoleOverviewMutation>(graphql`
    mutation WSEmployeeRoleOverviewMutation(
      $storecastId: ID!
      $widgetId: ID!
      $update: WidgetEmployeeRoleOverviewUpdateInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        updateWidgetEmployeeRoleOverview(id: $widgetId, update: $update) {
          id
          ...WidgetEmployeeRoleOverview_widget
          teams {
            id
            name
          }
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    save({
      variables: {
        storecastId: props.route.params.storecastId,
        update: {
          setTeamIds: teamIds,
        },
        widgetId: props.route.params.widgetId,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [
    props.navigation,
    props.route.params.storecastId,
    props.route.params.widgetId,
    save,
    teamIds,
  ]);

  const [query, setTeamIds] = useWidgetEmployeeRoleOverviewPreviewQuery({
    storecastId: props.route.params.storecastId,
    teamIds,
    setTeamIds: _setTeamIds,
  });

  return (
    <WidgetScreen
      settings={
        <WidgetOverviewSettings
          store={data.store}
          setTeamIds={setTeamIds}
          teamIds={teamIds}
        />
      }
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          loading={loading}
          size={props.route.params.size}
          unitSize={props.route.params.unitSize}
        >
          {query ? (
            <WidgetEmployeeRoleOverviewPreview
              query={query}
              storecastId={props.route.params.storecastId}
            />
          ) : null}
        </WidgetScreenPreview>
      }
    />
  );
}
